import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBreakpoints } from "@narmi/design_system";
import { entities } from "byzantine";
import Feature from "byzantine/src/Feature";

import Account from "byzantine/src/Account";
import NarmiTiles from "../../src/modules/narmiTiles";
import NpsSurvey from "../../src/modules/nps";
import PageLayout from "../../src/components/PageLayout";
import DashboardHeader from "./DashboardHeader";
import ChartCard from "./ChartCard";
import ServicesCard from "./ServicesCard";
import AccountTotalsCard from "./AccountTotalsCard";
import AccountBalances from "./AccountBalances";
import RecentTransactionsCard from "./RecentTransactionsCard";
import MarketingCardContainer from "./MarketingCardContainer";
import OnboardingCard from "./OnboardingCard";
import SavvyMoneyWidget from "../savvy_money/SavvyMoneyWidget";
import MarketingImage from "./MarketingImage";
import DashboardDialog from "./DashboardDialog";
import useIsDashboardDialogOpen from "./useIsDashboardDialogOpen";
import SubscriptionCard from "../subscriptions/SubscriptionCard";
import CardOffers from "./CardOffers";
import ExternalAccountTiles from "./ExternalAccountTiles";
import type { Institution } from "../../types";
import ArrayPlusWidget from "../ArrayPlus/ArrayPlusWidget";
import DashboardCustomization from "./DashboardCustomization/DashboardCustomization";
import { useVisibleWebDashboardSections } from "./DashboardCustomization/hooks";
import { renderLeftSection, renderRightSection } from "./Dashboard.sections";
import { MARKETING_CARD_IMAGE_ADDRESS } from "./constants";
import { DashboardSectionContext } from "./types";

import "./Dashboard.scss";
import useIsSubscriptionManagementEnabled from "../subscriptions/SubscriptionCard/hooks";
import ExternalAccountsContainer from "../../src/modules/dashboard/ExternalAccounts/ExternalAccountsContainer/ExternalAccountsContainer";

interface DashboardProps {
  androidUrl: string;
  appleUrl: string;
  institution: Institution;
  features: API.Features;
  replyTime: string;
  externalAccounts: Account[];
  uatMicrodepAmntOne?: string;
  uatMicrodepAmntTwo?: string;
}

const Dashboard = ({
  androidUrl,
  appleUrl,
  institution,
  features,
  replyTime,
  externalAccounts,
  uatMicrodepAmntOne,
  uatMicrodepAmntTwo,
}: DashboardProps) => {
  const { servicesApps, name } = institution;
  const { l } = useBreakpoints();
  const {
    send: fetchSavvyMoneyRedirectUrl,
    error: hasSavvyWidgetErrored,
    loading: isSavvyFetchLoading,
  } = entities.savvyMoney.useFetchSavvyMoneyRedirectUrl(
    entities.savvyMoney.SavvyMoneyViewUrl.WebWidget,
    Boolean(features.olb_enable_credit_monitor)
  );

  const isSubscriptionEnabled = useIsSubscriptionManagementEnabled();

  useEffect(() => {
    fetchSavvyMoneyRedirectUrl();
  }, []);

  const navigate = useNavigate();
  const isDialogOpen = useIsDashboardDialogOpen();
  const { visibleLeftSectionIds, visibleRightSectionIds } =
    useVisibleWebDashboardSections({ externalAccounts, servicesApps });

  const sectionContext: DashboardSectionContext = {
    features,
    externalAccounts,
    uatMicrodepAmntOne,
    uatMicrodepAmntTwo,
    replyTime,
    servicesApps,
    isSubscriptionEnabled,
    hasSavvyWidgetErrored,
    isSavvyFetchLoading,
    fetchSavvyMoneyRedirectUrl,
  };

  return (
    <>
      <PageLayout
        layout="dual-column"
        columnRatio="2fr minmax(380px, 1fr)"
        maxWidth="xl"
        header={<DashboardHeader />}
      >
        {l ? (
          <>
            <div>
              <OnboardingCard />
              {visibleLeftSectionIds.flatMap((id) =>
                renderLeftSection(id, sectionContext)
              )}
              <DashboardCustomization
                externalAccounts={externalAccounts}
                servicesApps={servicesApps}
              />
            </div>
            <div>
              {visibleRightSectionIds.flatMap((id) =>
                renderRightSection(id, sectionContext)
              )}
              <NarmiTiles />
            </div>
          </>
        ) : (
          <>
            {/* Single column for tablets and phones */}
            <div>
              <OnboardingCard />
              <AccountTotalsCard />
              <AccountBalances />
              <NarmiTiles />
              {features.enable_external_accounts_on_dashboard ? (
                <ExternalAccountsContainer
                  externalAccounts={externalAccounts}
                  uatMicrodepAmntOne={uatMicrodepAmntOne}
                  uatMicrodepAmntTwo={uatMicrodepAmntTwo}
                />
              ) : (
                <ExternalAccountTiles
                  externalAccounts={externalAccounts}
                  uatMicrodepAmntOne={uatMicrodepAmntOne}
                  uatMicrodepAmntTwo={uatMicrodepAmntTwo}
                />
              )}
              <MarketingCardContainer />
              <RecentTransactionsCard replyTime={replyTime} />
              <Feature features={features} or={["card_linked_offers"]}>
                <CardOffers />
              </Feature>
              <ChartCard />
              {features.array_plus_credit_reporting && <ArrayPlusWidget />}
              {features.olb_enable_credit_monitor && (
                <SavvyMoneyWidget
                  hasErrored={hasSavvyWidgetErrored}
                  refetch={fetchSavvyMoneyRedirectUrl}
                  isLoading={isSavvyFetchLoading}
                />
              )}
              {servicesApps && <ServicesCard services={servicesApps} />}
              {isSubscriptionEnabled && <SubscriptionCard />}
              <MarketingImage address={MARKETING_CARD_IMAGE_ADDRESS} />
            </div>
          </>
        )}
      </PageLayout>

      {/* Dialogs render outside PageLayout */}
      <DashboardDialog
        isOpen={isDialogOpen}
        fiName={name}
        androidUrl={androidUrl}
        appleUrl={appleUrl}
        closeDialog={() => navigate("/")}
      />
      <NpsSurvey />
    </>
  );
};

export default Dashboard;
